/**
 * Простои сотрудника за период: по дням часы, акцепт, % простоя и сами куски
 * простоя со ссылкой на смену дня. Спека `docs/specs/2026-09-24-idle-period-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";

import { loadIdle } from "@/lib/idle";
import { workTime } from "@/lib/leads";
import { PeriodBar, pct, useIdleParams } from "@/pages/IdlePage";

const WEEKDAYS = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const day = (d: string) => `${WEEKDAYS[new Date(`${d}T12:00:00`).getDay()]} ${d.slice(8, 10)}.${d.slice(5, 7)}`;
const hhmm = (iso: string) => iso.slice(11, 16);

export function IdleUserPage() {
  const userId = Number(useParams().userId);
  const [p, set] = useIdleParams();
  const q = useQuery({
    queryKey: ["idle-user", userId, p.from, p.to, p.location],
    queryFn: () => loadIdle({ ...p, user: userId }),
  });
  const person = q.data?.people[0];
  const back = `/idle?from=${p.from}&to=${p.to}&location=${p.location}`;

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <PeriodBar
        title={
          <>
            <Link to={back} className="text-fg-muted hover:text-fg">
              Простои
            </Link>{" "}
            / {person?.name ?? `#${userId}`}
          </>
        }
        p={p}
        set={set}
      />
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю простои по CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить простои: {String(q.error.message)}</div>
      ) : !person ? (
        <div className="text-[12.5px] text-fg-muted">За этот период у сотрудника нет ни часов, ни акцепта.</div>
      ) : (
        <>
          <div className="text-[13px] text-fg-muted" data-annot="итог сотрудника">
            Часы {workTime(person.paid_min)} · акцепт {workTime(person.accept_min)}
            {q.data.activity && (
              <>
                {" "}
                · простой {workTime(person.idle_min)} — <b>{pct(person.idle_pct)}</b> · пропущено в простоях{" "}
                {person.idle_missed}
              </>
            )}
            {person.pending_days > 0 && <span className="text-warn"> · без акцепта {person.pending_days} дн.</span>}
          </div>
          {person.days.map((d) => (
            <section
              key={d.date}
              className="rounded-[var(--radius-card)] border border-border bg-bg-subtle"
              data-annot={`день ${d.date}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-border px-4 py-2.5 text-[12.5px]">
                <span className="text-[13px] font-medium">{day(d.date)}</span>
                <span className="text-fg-muted">часы {workTime(d.paid_min)}</span>
                <span className="text-fg-muted">
                  акцепт {d.accepted ? workTime(d.accept_min) : <span className="text-warn">не акцептован</span>}
                </span>
                {q.data.activity && d.accepted && (
                  <span className="text-fg-muted">
                    простой {workTime(d.idle_min)} — <b>{pct(d.idle_pct)}</b>
                  </span>
                )}
                <Link
                  to={`/activity/${userId}?date=${d.date}`}
                  className="ml-auto text-[12px] text-fg-muted underline decoration-dotted underline-offset-2 hover:text-fg"
                >
                  смена дня →
                </Link>
              </div>
              {d.idle.length > 0 && (
                <table className="w-full text-[12.5px]">
                  <tbody>
                    {d.idle.map((g) => (
                      <tr key={g.from} className="border-t border-border first:border-t-0" data-annot={`простой ${hhmm(g.from)}`}>
                        <td className="w-32 whitespace-nowrap px-4 py-1.5 tabular-nums">
                          {hhmm(g.from)}–{hhmm(g.to)}
                        </td>
                        <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">{workTime(g.minutes)}</td>
                        <td className="w-full px-2.5 py-1.5">
                          <div className="h-1.5 rounded bg-err/70" style={{ width: `${Math.min(g.minutes / 240, 1) * 100}%` }} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-1.5 text-right tabular-nums">
                          {g.missed ? <span className="text-warn">пропущено {g.missed}</span> : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          ))}
        </>
      )}
    </main>
  );
}
